import {
  Award,
  BadgeDollarSign,
  Camera,
  ChefHat,
  Clapperboard,
  FileBadge,
  GraduationCap,
  Handshake,
  Megaphone,
  MicVocal,
  Network,
  Rocket,
  ShieldCheck,
  ShoppingBag,
  Store,
  Trophy,
  Users,
  UtensilsCrossed,
  WandSparkles,
  type LucideIcon,
} from "lucide-react"

export type BenefitIconKey =
  | "megaphone"
  | "badge-dollar-sign"
  | "users"
  | "handshake"
  | "shield-check"
  | "network"
  | "shopping-bag"
  | "award"
  | "file-badge"
  | "graduation-cap"
  | "store"
  | "trophy"
  | "mic-vocal"
  | "chef-hat"
  | "utensils-crossed"
  | "clapperboard"
  | "wand-sparkles"
  | "camera"
  | "rocket"

export const BENEFIT_ICONS: Record<BenefitIconKey, LucideIcon> = {
  megaphone: Megaphone,
  "badge-dollar-sign": BadgeDollarSign,
  users: Users,
  handshake: Handshake,
  "shield-check": ShieldCheck,
  network: Network,
  "shopping-bag": ShoppingBag,
  award: Award,
  "file-badge": FileBadge,
  "graduation-cap": GraduationCap,
  store: Store,
  trophy: Trophy,
  "mic-vocal": MicVocal,
  "chef-hat": ChefHat,
  "utensils-crossed": UtensilsCrossed,
  clapperboard: Clapperboard,
  "wand-sparkles": WandSparkles,
  camera: Camera,
  rocket: Rocket,
}

export const BENEFIT_ICON_OPTIONS = Object.keys(BENEFIT_ICONS) as BenefitIconKey[]

export function isBenefitIconKey(value: string): value is BenefitIconKey {
  return (BENEFIT_ICON_OPTIONS as string[]).includes(value)
}

export type BenefitItem = {
  text: string
  icon: BenefitIconKey
}

export type PricingGalleryImage = {
  image_url: string
  alt: string
}

export type ReasonItem = {
  title: string
  description: string
  icon: BenefitIconKey
}

export type ActionBenefitItem = {
  title: string
  subtitle: string
}

export type BenefitTranslatable = {
  pricing_title: string
  pricing_badge_text: string
  pricing_amount: string
  pricing_body: string
  pricing_cta_label: string
  pricing_features: string[]
  benefits_title: string
  benefits_description: string
  benefits: BenefitItem[]
  reasons_badge_text: string
  reasons_title: string
  reasons_description: string
  reasons: ReasonItem[]
  action_benefit_eyebrow: string
  action_benefit_title: string
  action_benefit_description: string
  action_benefits: ActionBenefitItem[]
}

export type BenefitSection = {
  translations: Record<string, BenefitTranslatable>
  pricing_gallery: PricingGalleryImage[]
  pricing_area_image_url: string | null
  benefits_image_url: string | null
  is_active: boolean
}

export const DEFAULT_LANGUAGE_CODE = "km"

export const EMPTY_BENEFIT_TRANSLATION: BenefitTranslatable = {
  pricing_title: "",
  pricing_badge_text: "",
  pricing_amount: "",
  pricing_body: "",
  pricing_cta_label: "",
  pricing_features: [],
  benefits_title: "",
  benefits_description: "",
  benefits: [],
  reasons_badge_text: "",
  reasons_title: "",
  reasons_description: "",
  reasons: [],
  action_benefit_eyebrow: "",
  action_benefit_title: "",
  action_benefit_description: "",
  action_benefits: [],
}

export const BENEFIT_SECTION_DEFAULTS: BenefitSection = {
  translations: {
    km: {
      pricing_title: "ថវិកាចូលរួម និងអត្ថប្រយោជន៍ដែលទទួលបាន",
      pricing_badge_text: "ឱកាសពិសេសចំណាយត្រឹមតែ",
      pricing_amount: "$300",
      pricing_body:
        "សម្រាប់ម្ចាស់អាជីវកម្មអាហារ និងម៉ាកដែលចង់ចូលរួមយ៉ាងមានសេចក្តីត្រៀម និងទទួលបានការគាំទ្រពេញលេញ។",
      pricing_cta_label: "សួរព័ត៌មានតម្លៃស្តង់",
      pricing_features: [
        "ការបណ្តុះបណ្តាលពេញលេញ",
        "Booth តាំងពិព័រណ៍ 3m x 3m",
        "ការផ្សព្វផ្សាយ Social Media",
        "ឱកាសពង្រីកអាជីវកម្ម",
        "ការកសាងកេរឈ្មោះ",
        "ការគាំទ្រមុន និងក្នុងអំឡុងពេលកម្មវិធី",
      ],
      benefits_title: "អត្ថប្រយោជន៍សម្រាប់ម្ចាស់ស្តង់",
      benefits_description:
        "ម្ចាស់ស្តង់នឹងទទួលបានឱកាសសំខាន់ៗក្នុងការលក់ ផ្សព្វផ្សាយម៉ាកយីហោ ភ្ជាប់ទំនាក់ទំនង និងពង្រីកអាជីវកម្ម។",
      benefits: [
        { text: "ពង្រីកទីផ្សារ", icon: "megaphone" },
        { text: "បង្កើនការលក់", icon: "badge-dollar-sign" },
        { text: "ទទួលបានលិខិតថ្លែងអំណរគុណ", icon: "users" },
        { text: "ពង្រឹងម៉ាកយីហោនៅក្នុងព្រឹត្តិការណ៍កម្រិតជាតិ", icon: "handshake" },
        { text: "ក្លាយជាសមាជិកសហគមន៍សហគ្រិនអាហារ", icon: "shield-check" },
        { text: "ភ្ជាប់ទំនាក់ទំនងជាមួយ CEO Master Club", icon: "network" },
        { text: "មានឱកាសឈ្នះពានរង្វាន់ “សហគ្រិនឆ្នើមវិស័យអាហារ”", icon: "shopping-bag" },
        { text: "ភ្ជាប់ទំនាក់ទំនងផ្ទាល់ជាមួយអតិថិជន និងអ្នកបោះដុំ", icon: "award" },
        { text: "ទទួលបានការប្រឹក្សា និងការបណ្តុះបណ្តាល", icon: "file-badge" },
      ],
      reasons_badge_text: "អត្ថប្រយោជន៍សម្រាប់សហគ្រិន",
      reasons_title: "ហេតុអ្វីអាជីវករអាហារខ្មែរត្រូវចូលរួម?",
      reasons_description:
        "កម្មវិធីនេះបង្កើតវេទិកាសម្រាប់កសាងសមត្ថភាព បង្កើតទំនាក់ទំនង ពង្រីកទីផ្សារ និងពង្រឹងភាពជឿជាក់របស់ម៉ាកអាហារខ្មែរ។",
      reasons: [
        { title: "ការបង្វឹកសហគ្រិន", description: "ទទួលបានការបង្វឹកពី វិទ្យាស្ថានស៊ីអ៊ីអូ ក្នុងការកសាងខ្លួនក្លាយជាសហគ្រិនអាហារឆ្នើម", icon: "graduation-cap" },
        { title: "ដាក់តាំងពិព័រណ៍ផលិតផល", description: "ដាក់តាំងពិព័រណ៍ផលិតផល ដើម្បីកសាងកេរ្តិ៍ឈ្មោះអាជីវកម្ម និងបង្កើនការលក់ឱ្យបានច្រើន", icon: "store" },
        { title: "ប្រកួតប្រជែងសមត្ថភាព", description: "ហ្វឹកហាត់ និងប្រកួតប្រជែងសមត្ថភាពរៀបចំគុណភាពផលិតផល ការវេចខ្ចប់ ការរៀបចំកន្លែងតាំងលក់ និងសេវាកម្មរហ័ស", icon: "trophy" },
        { title: "សហគមន៍សហគ្រិន", description: "ក្លាយជាសមាជិកសហគមន៍សហគ្រិនអាហារកម្ពុជា ដែលមានឱកាសត្រៀមខ្លួនសហការពង្រីកអាជីវកម្មបន្ថែមទៀត", icon: "users" },
        { title: "ប្រឡងចម្រៀង", description: "ចូលរួមប្រឡងចម្រៀងលើកតម្កើងវិស័យ សិល្បៈ និងវប្បធម៌អាហារកម្ពុជា", icon: "mic-vocal" },
        { title: "ប្រឡងធ្វើម្ហូប", description: "ចូលរួមប្រឡងធ្វើម្ហូប ដើម្បីលើកកេរ្តិ៍ឈ្មោះអាជីវកម្មរបស់ខ្លួន", icon: "chef-hat" },
        { title: "KOLs ចូលរួម", description: "មាន KOLs ជាដៃគូប្រឡងធ្វើម្ហូបអាហារខ្មែរកូនកាត់", icon: "handshake" },
        { title: "សិល្បករ និងតារាកំប្លែង", description: "មានសិល្បករ តារាកំប្លែង និងបុគ្គលល្បីៗជាច្រើនចូលរួម", icon: "award" },
        { title: "ប្រកួតហូបទុរេន", description: "មានការប្រកួតហូបទុរេននៅខាងក្រៅ ដើម្បីទាក់ទាញភ្ញៀវចូលមកខាងក្នុង", icon: "utensils-crossed" },
        { title: "ប្រកួតចម្រៀង W-King", description: "មានការប្រកួតចម្រៀង W-King នៅខាងក្រៅ ដើម្បីទាក់ទាញភ្ញៀវចូលមកខាងក្នុង", icon: "clapperboard" },
        { title: "Cooking Show", description: "មានការសម្តែងរបៀបធ្វើម្ហូបប្លែកៗបែបសិល្បៈ និងសប្បាយៗនៅខាងក្រៅ ដើម្បីទាក់ទាញភ្ញៀវចូលមកខាងក្នុង", icon: "wand-sparkles" },
        { title: "ថតរូប និងវីដេអូ", description: "ថតចងក្រងជារូបភាព និងវីដេអូ ទុកជាប្រវត្តិសាស្រ្ត និងការផ្សព្វផ្សាយអាជីវកម្មរយៈពេលយូរអង្វែង", icon: "camera" },
        { title: "ពង្រីកអាជីវកម្ម", description: "សហគ្រិនអាហារឆ្នើមអាចមានឱកាសពង្រីកអាជីវកម្មជាមួយដៃគូសហការ ទៅតាម Food Court ឬ Hawker Center នាពេលខាងមុខ", icon: "rocket" },
        { title: "ទស្សនិកជន 30,000+", description: "មានអ្នកចូលរួមក្នុងកម្មវិធីទាំងមូលសម្រាប់រយៈពេល ៣ ថ្ងៃ ចំនួនយ៉ាងតិច 30,000 នាក់", icon: "users" },
        { title: "ផ្សព្វផ្សាយដ៏ធំ", description: "ជាកម្មវិធីដែលមានការផ្សព្វផ្សាយច្រើនបំផុត ទាំងអ្នករៀបចំកម្មវិធី អ្នកសារព័ត៌មាន សិល្បករ KOLs និងអ្នកដាក់ស្តង់លក់ទាំងអស់គ្នា", icon: "megaphone" },
      ],
      action_benefit_eyebrow: "Action Benefit",
      action_benefit_title: "ភាពរួមនៃសកម្មភាព និងអត្ថប្រយោជន៍",
      action_benefit_description:
        "សូមមើលទិដ្ឋភាពសំខាន់ៗទាំង ៥ ដើម្បីយល់ពីអ្វីដែលនឹងមាននៅក្នុង ពិព័រណ៍ និងកម្មវិធីគាំទ្រផ្សេងៗ។",
      action_benefits: [
        { title: "ទទួលបានការបង្វឹកពីវិទ្យាស្ថានស៊ីអ៊ីអូ", subtitle: "អាជីវកម្មអាហារខ្មែរនឹងទទួលបានការបង្វឹកពី វិទ្យាស្ថានស៊ីអ៊ីអូ នៅថ្ងៃទី 29 ខែមិថុនា ឆ្នាំ 2026 និងថ្ងៃទី 11 ខែកក្កដា ឆ្នាំ 2026 ក្រោមប្រធានបទ «ការកសាងខ្លួន ក្លាយជាសហគ្រិនឆ្នើម»។" },
        { title: "ដាក់តាំងពិព័រណ៍ផលិតផល", subtitle: "ចូលរួមដាក់តាំងផលិតផល នៅថ្ងៃទី 17, 18, 19 ខែកក្កដា ឆ្នាំ 2026 ដើម្បីកសាងកេរ្តិ៍ឈ្មោះអាជីវកម្ម បង្កើនការស្គាល់ម៉ាកយីហោ និងជំរុញការលក់ឱ្យបានច្រើន។" },
        { title: "ហ្វឹកហាត់ និងប្រកួតប្រជែងសមត្ថភាព", subtitle: "សហគ្រិននឹងមានឱកាសហ្វឹកហាត់ និងប្រកួតប្រជែងលើការរៀបចំគុណភាពផលិតផល ការវេចខ្ចប់ ការរៀបចំកន្លែងតាំងលក់ សេវាកម្មរហ័ស និងការផ្សព្វផ្សាយ ដើម្បីបង្កើនកេរ្តិ៍ឈ្មោះអាជីវកម្មឱ្យកាន់តែល្បី។" },
        { title: "ក្លាយជាសមាជិកសហគមន៍សហគ្រិនអាហារកម្ពុជា", subtitle: "ចូលរួមជាសមាជិកសហគមន៍សហគ្រិនអាហារកម្ពុជា ដើម្បីមានឱកាសរៀនសូត្រ បង្កើតបណ្តាញទំនាក់ទំនង និងត្រៀមខ្លួនសម្រាប់ការពង្រីកអាជីវកម្មបន្ថែមទៀត។" },
        { title: "ឱកាសពង្រីកអាជីវកម្មជាមួយដៃគូសហការ", subtitle: "សហគ្រិនអាហារឆ្នើមៗអាចមានឱកាសពង្រីកអាជីវកម្មរបស់ខ្លួនជាមួយដៃគូសហការ ទៅកាន់ Food Court ឬ Hawker Center នាពេលខាងមុខ។" },
      ],
    },
    en: {
      pricing_title: "Participation Cost & Benefits",
      pricing_badge_text: "Special Offer, Only",
      pricing_amount: "$300",
      pricing_body:
        "For food business owners and brands who want to participate fully prepared and receive complete support.",
      pricing_cta_label: "Ask About Booth Pricing",
      pricing_features: [
        "Full training",
        "3m x 3m exhibition booth",
        "Social media promotion",
        "Business expansion opportunities",
        "Brand building",
        "Support before and during the program",
      ],
      benefits_title: "Benefits for Booth Owners",
      benefits_description:
        "Booth owners will gain key opportunities to sell, promote their brand, build connections, and expand their business.",
      benefits: [
        { text: "Expand your market", icon: "megaphone" },
        { text: "Increase sales", icon: "badge-dollar-sign" },
        { text: "Receive a certificate of appreciation", icon: "users" },
        { text: "Strengthen your brand at a national-level event", icon: "handshake" },
        { text: "Become a member of the food entrepreneur community", icon: "shield-check" },
        { text: "Connect with CEO Master Club", icon: "network" },
        { text: "Chance to win the “Outstanding Food Entrepreneur” award", icon: "shopping-bag" },
        { text: "Connect directly with customers and wholesalers", icon: "award" },
        { text: "Receive consulting and training", icon: "file-badge" },
      ],
      reasons_badge_text: "Benefits for Entrepreneurs",
      reasons_title: "Why Should Khmer Food Businesses Join?",
      reasons_description:
        "This program creates a platform for building capacity, forming connections, expanding markets, and strengthening trust in Khmer food brands.",
      reasons: [
        { title: "Entrepreneur Coaching", description: "Receive coaching from the CEO Institute to become an outstanding food entrepreneur", icon: "graduation-cap" },
        { title: "Product Exhibition", description: "Exhibit your products to build your business reputation and boost sales", icon: "store" },
        { title: "Skills Competition", description: "Practice and compete in product quality, packaging, booth setup, and fast service", icon: "trophy" },
        { title: "Entrepreneur Community", description: "Become a member of the Cambodian food entrepreneur community, with opportunities to prepare for further business expansion", icon: "users" },
        { title: "Singing Competition", description: "Join a singing competition celebrating Cambodian food arts and culture", icon: "mic-vocal" },
        { title: "Cooking Competition", description: "Join a cooking competition to build your business's reputation", icon: "chef-hat" },
        { title: "KOLs Participating", description: "KOLs will be partners in a fusion Khmer cooking competition", icon: "handshake" },
        { title: "Artists & Comedians", description: "Many artists, comedians, and celebrities will take part", icon: "award" },
        { title: "Durian-Eating Contest", description: "An outdoor durian-eating contest to attract visitors inside", icon: "utensils-crossed" },
        { title: "W-King Singing Contest", description: "An outdoor W-King singing contest to attract visitors inside", icon: "clapperboard" },
        { title: "Cooking Show", description: "Artistic and fun cooking demonstrations outdoors to attract visitors inside", icon: "wand-sparkles" },
        { title: "Photo & Video", description: "Photos and videos captured as a historical record and long-term business promotion", icon: "camera" },
        { title: "Business Expansion", description: "Outstanding food entrepreneurs may get the chance to expand their business with partners via Food Courts or Hawker Centers", icon: "rocket" },
        { title: "30,000+ Audience", description: "At least 30,000 people are expected to attend the program over 3 days", icon: "users" },
        { title: "Massive Promotion", description: "The program with the most promotion — organizers, journalists, artists, KOLs, and all booth owners together", icon: "megaphone" },
      ],
      action_benefit_eyebrow: "Action Benefit",
      action_benefit_title: "Activities & Benefits Overview",
      action_benefit_description:
        "Take a look at these 5 key highlights to understand what to expect at the exhibition and various support programs.",
      action_benefits: [
        { title: "Receive Coaching from the CEO Institute", subtitle: "Khmer food businesses will receive coaching from the CEO Institute on June 29, 2026 and July 11, 2026 under the theme “Building Yourself into an Outstanding Entrepreneur.”" },
        { title: "Exhibit Your Products", subtitle: "Join the product exhibition on July 17, 18, 19, 2026 to build your business reputation, increase brand recognition, and boost sales." },
        { title: "Train & Compete in Skills", subtitle: "Entrepreneurs will have the chance to train and compete in product quality, packaging, booth setup, fast service, and promotion to build a more well-known business reputation." },
        { title: "Become a Member of the Cambodian Food Entrepreneur Community", subtitle: "Join the Cambodian food entrepreneur community for learning opportunities, networking, and preparation for further business expansion." },
        { title: "Business Expansion Opportunities with Partners", subtitle: "Outstanding food entrepreneurs may get the chance to expand their business with partners via Food Courts or Hawker Centers in the future." },
      ],
    },
  },
  pricing_gallery: [
    { image_url: "/images/cam-food-expo/new/live1.jpg", alt: "Live 1" },
    { image_url: "/images/cam-food-expo/new/live2.jpg", alt: "Live 2" },
    { image_url: "/images/cam-food-expo/new/live3.jpg", alt: "Live 3" },
    { image_url: "/images/cam-food-expo/new/food-cam.jpg", alt: "Food cam" },
  ],
  pricing_area_image_url: "/images/cam-food-expo/new/area.png",
  benefits_image_url: "/images/cam-food-expo/new/image-cam.png",
  is_active: true,
}

export function getBenefitTranslation(
  data: Pick<BenefitSection, "translations">,
  languageCode: string
): BenefitTranslatable {
  const entry = data.translations[languageCode] ?? data.translations[DEFAULT_LANGUAGE_CODE]
  return { ...EMPTY_BENEFIT_TRANSLATION, ...entry }
}
